import React from 'react';

import { Document, Text, View } from '@react-pdf/renderer';

import { fmtDateTimeLong, fmtQty, sum } from '../format';
import {
  PO_STATUS,
  PO_TYPE_META,
  poSourceOf,
  poTypeOf,
  RECEIPT_STATUS,
} from '../status';
import type { PurchaseOrderDetail, PurchaseOrderLine } from '../types';
import {
  PdfA4,
  PdfBlock,
  type PdfColumn,
  PdfFooter,
  PdfHeader,
  PdfKV,
  PdfKVRow,
  PdfSignatures,
  PdfTable,
  pdfStyles,
  type PrintMeta,
  T,
} from './pdfKit';

/**
 * Purchase order (not cancelled): client, source (vendor / source hub /
 * returned from — labelled by PO type), ship-to hub, ETA, lines.
 */
const PurchaseOrderPdf: React.FC<{
  detail: PurchaseOrderDetail;
  hubAddress: string | null;
  meta: PrintMeta;
}> = ({ detail, hubAddress, meta }) => {
  const po = detail.order;
  const type = poTypeOf(po);
  const source = poSourceOf(po);
  const columns: PdfColumn<PurchaseOrderLine>[] = [
    { title: 'No', width: 20, fixed: true, render: (_l, i) => <T>{i + 1}</T> },
    {
      title: 'SKU',
      width: 1.6,
      render: (l) => (
        <T mono bold>
          {l.sku}
        </T>
      ),
    },
    { title: 'Nama barang', width: 3.2, render: (l) => <T>{l.itemName}</T> },
    {
      title: 'Qty',
      width: 0.9,
      align: 'right',
      render: (l) => <T>{fmtQty(l.qty)}</T>,
    },
    { title: 'UoM', width: 0.9, render: (l) => <T>{l.uomName}</T> },
  ];

  return (
    <Document title={`Purchase order ${po.code}`}>
      <PdfA4>
        <PdfHeader
          title="Purchase order"
          code={po.code}
          meta={`Dibuat ${fmtDateTimeLong(po.createdAt)} · ${PO_STATUS[po.status]?.label ?? po.status} · Tipe ${PO_TYPE_META[type].label}`}
          qr={meta.qr}
        />
        <View style={[pdfStyles.row, { marginTop: 12 }]}>
          <PdfBlock title="Client (pemilik barang)">
            <Text style={pdfStyles.value}>
              {po.client?.name ?? `Client #${po.clientID}`}
            </Text>
          </PdfBlock>
          <PdfBlock title={source.label}>
            <Text style={pdfStyles.value}>{source.value || '—'}</Text>
            {source.sub ? (
              <Text
                style={[pdfStyles.small, pdfStyles.muted, { marginTop: 2 }]}
              >
                {source.sub}
              </Text>
            ) : null}
            {type === 'TRANSFER' && po.transferOutboundOrder ? (
              <Text
                style={[pdfStyles.small, pdfStyles.muted, { marginTop: 2 }]}
              >
                Outbound {po.transferOutboundOrder.code}
              </Text>
            ) : null}
          </PdfBlock>
          <PdfBlock title="Kirim ke" last>
            <Text style={pdfStyles.value}>
              {po.hub?.name ?? `Hub #${po.hubID}`}
            </Text>
            {hubAddress ? (
              <Text
                style={[pdfStyles.small, pdfStyles.muted, { marginTop: 2 }]}
              >
                {hubAddress}
              </Text>
            ) : null}
          </PdfBlock>
        </View>
        <PdfKVRow>
          <PdfKV
            label="Estimasi tiba"
            value={po.estArrivalAt ? fmtDateTimeLong(po.estArrivalAt) : null}
          />
          <PdfKV
            label="Status penerimaan"
            value={
              po.receiptStatus ? RECEIPT_STATUS[po.receiptStatus]?.label : null
            }
          />
          <PdfKV
            label="Total qty"
            value={fmtQty(sum(detail.lines.map((l) => l.qty)))}
          />
        </PdfKVRow>
        <PdfTable columns={columns} rows={detail.lines} />
        {po.notes ? (
          <Text style={[pdfStyles.small, { marginTop: 8 }]}>
            <Text style={pdfStyles.label}>Catatan </Text>
            {po.notes}
          </Text>
        ) : null}
        <Text style={pdfStyles.note}>
          Mohon sertakan nomor PO di surat jalan pengiriman. Barang diterima
          sesuai qty aktual saat goods receipt.
        </Text>
        <PdfSignatures labels={['Dibuat oleh', 'Disetujui client']} />
        <PdfFooter
          doc={`Purchase order ${po.code}`}
          printedAt={meta.printedAt}
          printedBy={meta.printedBy}
        />
      </PdfA4>
    </Document>
  );
};

export default PurchaseOrderPdf;
